import React, { useMemo } from "react";
import { hasValidSizeChart, normalizeSizeChartUnit } from "../../../utils/sizeChartUtils";

const EMPTY_CHART = {
  unit: "cm",
  title: "",
  arTitle: "",
  note: "",
  arNote: "",
  columns: [{ label: "Size", arLabel: "المقاس" }],
  rows: [[""]],
};

function normalizeChart(value) {
  if (!value || typeof value !== "object") return { ...EMPTY_CHART };
  const unit = normalizeSizeChartUnit(value.unit);
  const title = String(value.title || "");
  const arTitle = String(value.arTitle || "");
  const note = String(value.note || "");
  const arNote = String(value.arNote || "");
  const columnsRaw = Array.isArray(value.columns) ? value.columns : [];
  const columns =
    columnsRaw.length > 0
      ? columnsRaw.map((c) => ({
          label: String(c?.label || c || ""),
          arLabel: String(c?.arLabel || ""),
        }))
      : [...EMPTY_CHART.columns];
  const rowsRaw = Array.isArray(value.rows) ? value.rows : [];
  const rows = rowsRaw.length > 0 ? rowsRaw.map((r) => (Array.isArray(r) ? r.map((x) => String(x ?? "")) : [""])) : [[""]];
  return { unit, title, arTitle, note, arNote, columns, rows };
}

function isEffectivelyEmpty(chart) {
  if (!chart) return true;
  const cols = Array.isArray(chart.columns) ? chart.columns : [];
  const rows = Array.isArray(chart.rows) ? chart.rows : [];
  if (cols.length === 0 || rows.length === 0) return true;
  const anyHeader = cols.some((c) => String(c?.label || c?.arLabel || "").trim());
  const anyCell = rows.some((r) => Array.isArray(r) && r.some((x) => String(x || "").trim()));
  return !anyHeader && !anyCell && !String(chart.title || chart.arTitle || "").trim();
}

export function SizeChartEditor({ value, onChange }) {
  const chart = useMemo(() => normalizeChart(value), [value]);
  const isValid = useMemo(() => hasValidSizeChart(chart), [chart]);

  const update = (next) => onChange?.(next);

  const setField = (key, v) => update({ ...chart, [key]: v });

  const addColumn = () => {
    const nextCols = [...chart.columns, { label: "", arLabel: "" }];
    const nextRows = chart.rows.map((r) => [...r, ""]);
    update({ ...chart, columns: nextCols, rows: nextRows });
  };

  const removeColumn = (idx) => {
    if (chart.columns.length <= 1) return;
    const nextCols = chart.columns.filter((_, i) => i !== idx);
    const nextRows = chart.rows.map((r) => r.filter((_, i) => i !== idx));
    update({ ...chart, columns: nextCols, rows: nextRows });
  };

  const addRow = () => {
    update({ ...chart, rows: [...chart.rows, Array.from({ length: chart.columns.length }).map(() => "")] });
  };

  const removeRow = (idx) => {
    if (chart.rows.length <= 1) return;
    update({ ...chart, rows: chart.rows.filter((_, i) => i !== idx) });
  };

  const setCell = (rIdx, cIdx, v) => {
    const nextRows = chart.rows.map((r, i) => {
      if (i !== rIdx) return r;
      return r.map((cell, j) => (j === cIdx ? v : cell));
    });
    update({ ...chart, rows: nextRows });
  };

  const setColHeader = (idx, key, v) => {
    const nextCols = chart.columns.map((c, i) => (i === idx ? { ...c, [key]: v } : c));
    update({ ...chart, columns: nextCols });
  };

  const normalizeTable = () => {
    const colsLen = chart.columns.length;
    const nextRows = (Array.isArray(chart.rows) ? chart.rows : []).map((r) => {
      const arr = Array.isArray(r) ? r : [];
      if (arr.length === colsLen) return arr;
      if (arr.length > colsLen) return arr.slice(0, colsLen);
      return [...arr, ...Array.from({ length: colsLen - arr.length }).map(() => "")];
    });
    update({ ...chart, rows: nextRows.length ? nextRows : [Array.from({ length: colsLen }).map(() => "")] });
  };

  const mismatchCount = useMemo(() => {
    const colsLen = chart.columns.length;
    return (Array.isArray(chart.rows) ? chart.rows : []).filter((r) => (Array.isArray(r) ? r.length : 0) !== colsLen).length;
  }, [chart.columns.length, chart.rows]);

  const clear = () => update(null);

  return (
    <div className="input">
      <label>Size chart (optional)</label>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <select value={chart.unit} onChange={(e) => setField("unit", e.target.value)}>
          <option value="mm">mm</option>
          <option value="cm">cm</option>
          <option value="in">in</option>
        </select>
        <button type="button" onClick={addColumn}>
          + Column
        </button>
        <button type="button" onClick={addRow}>
          + Row
        </button>
        <button type="button" onClick={normalizeTable} disabled={chart.columns.length < 1}>
          Normalize
        </button>
        <button type="button" onClick={clear} disabled={isEffectivelyEmpty(chart)}>
          Clear
        </button>
      </div>
      {mismatchCount > 0 ? (
        <div style={{ fontSize: 12, color: "#9a7462", fontWeight: 600, marginBottom: 8 }}>
          {mismatchCount} row(s) have a different number of cells than the column count. Click “Normalize” to fix.
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input
            value={chart.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Title (EN)"
          />
          <input
            value={chart.arTitle}
            onChange={(e) => setField("arTitle", e.target.value)}
            placeholder="العنوان (AR)"
            dir="rtl"
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input
            value={chart.note}
            onChange={(e) => setField("note", e.target.value)}
            placeholder="Note (EN)"
          />
          <input
            value={chart.arNote}
            onChange={(e) => setField("arNote", e.target.value)}
            placeholder="ملاحظة (AR)"
            dir="rtl"
          />
        </div>
      </div>

      <div style={{ overflowX: "auto", marginTop: 12, border: "1px solid #eee", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
          <thead>
            <tr>
              {chart.columns.map((c, idx) => (
                <th key={idx} style={{ padding: 10, borderBottom: "1px solid #eee", textAlign: "left" }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <input
                      value={c.label}
                      onChange={(e) => setColHeader(idx, "label", e.target.value)}
                      placeholder={`Header ${idx + 1} (EN)`}
                    />
                    <input
                      value={c.arLabel}
                      onChange={(e) => setColHeader(idx, "arLabel", e.target.value)}
                      placeholder={`عنوان ${idx + 1} (AR)`}
                      dir="rtl"
                    />
                    <div>
                      <button type="button" onClick={() => removeColumn(idx)} disabled={chart.columns.length <= 1}>
                        Remove
                      </button>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {chart.rows.map((row, rIdx) => (
              <tr key={rIdx}>
                {row.map((cell, cIdx) => (
                  <td key={cIdx} style={{ padding: 10, borderBottom: "1px solid #f1f1f1" }}>
                    <input
                      value={cell}
                      onChange={(e) => setCell(rIdx, cIdx, e.target.value)}
                      placeholder="Value"
                      style={{ width: "100%" }}
                    />
                  </td>
                ))}
                <td style={{ padding: 10, borderBottom: "1px solid #f1f1f1", width: 90 }}>
                  <button type="button" onClick={() => removeRow(rIdx)} disabled={chart.rows.length <= 1}>
                    Remove row
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 14, border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#faf7f3" }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Preview</div>
        <div style={{ fontSize: 12, color: "#6b6560", marginBottom: 10 }}>
          Customer view preview. Unit: <strong>{chart.unit}</strong>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid rgba(92, 74, 58, 0.12)", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 520 }}>
            <thead>
              <tr>
                {chart.columns.map((c, idx) => (
                  <th
                    key={`p-${idx}`}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      padding: "10px 12px",
                      textAlign: "left",
                      background: "#f8f6f3",
                      borderBottom: "1px solid rgba(92, 74, 58, 0.12)",
                      fontWeight: 900,
                    }}
                  >
                    {String(c?.label || c?.arLabel || "").trim() || `Column ${idx + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.rows.map((r, i) => (
                <tr key={`pr-${i}`}>
                  {(Array.isArray(r) ? r : []).map((cell, j) => (
                    <td
                      key={`pc-${i}-${j}`}
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid rgba(92, 74, 58, 0.08)",
                        fontSize: 13,
                      }}
                    >
                      {String(cell ?? "").trim() || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
        Saved as one optional chart per product. {isValid ? "" : "If left empty, it will be hidden on the product page."}
      </div>
    </div>
  );
}

